import React from "react";
import { Link } from "react-router-dom";
import { formatCurrency, formatDate } from "../../lib/api";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import TrendCell from "./TrendCell";

const TIER = {
  low: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  high: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

function Schedule({ p }) {
  if (p.days_late == null) return <span className="text-slate-500">{p.planned_delivery ? formatDate(p.planned_delivery) : "—"}</span>;
  return (
    <span>
      <span className="text-slate-200">{formatDate(p.planned_delivery)}</span>
      <span className={`block text-[11px] ${p.days_late > 0 ? "text-rose-300" : "text-emerald-300"}`}>
        {p.days_late > 0 ? `${p.days_late}d late` : `${Math.abs(p.days_late)}d ahead`} · due {formatDate(p.stated_due_date)}
      </span>
    </span>
  );
}

function Row({ p }) {
  const over = p.stated_budget > 0 && p.estimated_cost > p.stated_budget;
  return (
    <tr className={`hover:bg-slate-900/40 ${p.is_open ? "" : "opacity-60"}`} data-testid={`exec-project-row-${p.id}`}>
      <td className="p-3"><Link to={`/app/projects/${p.id}`} className="text-white font-medium hover:text-blue-300" data-testid={`exec-project-link-${p.id}`}>{p.name}</Link>
        <span className="block text-[11px] text-slate-500">{p.program_name || "No program"}</span></td>
      <td className="p-3 text-slate-300">{p.pm_name || <span className="text-slate-500">Unassigned</span>}
        <span className="block text-[11px] text-slate-500" data-testid={`exec-owner-${p.id}`}>Owner: {p.project_owner_name || "Unassigned"}</span></td>
      <td className="p-3"><Schedule p={p} /></td>
      <td className="p-3 mono"><span className={over ? "text-rose-300" : "text-slate-200"}>{formatCurrency(p.estimated_cost)}</span>
        <span className="block text-[11px] text-slate-500">of {p.stated_budget ? formatCurrency(p.stated_budget) : "no budget"}</span></td>
      <td className="p-3 min-w-[120px]">
        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-blue-500" style={{ width: `${p.percent_complete}%` }} /></div>
        <span className="text-[11px] text-slate-400">{p.percent_complete}% · {p.completed_count}/{p.task_count} tasks</span>
      </td>
      <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${TIER[p.risk_tier]}`} data-testid={`exec-risk-${p.id}`}>{p.risk_tier}</span></td>
      <td className="p-3"><TrendCell p={p} /></td>
      <td className="p-3" data-testid={`exec-health-${p.id}`}>
        {!p.is_open ? <span className="text-xs text-slate-400">Completed</span>
          : p.health === "at_risk"
            ? <span className="inline-flex items-center gap-1 text-xs text-rose-300"><AlertTriangle size={12} /> At risk</span>
            : <span className="inline-flex items-center gap-1 text-xs text-emerald-300"><CheckCircle2 size={12} /> On track</span>}
      </td>
    </tr>
  );
}

export default function ExecProjectsTable({ projects }) {
  const open = projects.filter((p) => p.is_open);
  const closed = projects.filter((p) => !p.is_open);
  return (
    <div className="pc-card overflow-x-auto" data-testid="exec-projects-table">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-widest text-blue-300/80 border-b border-slate-800">
          <tr><th className="p-3">Project</th><th className="p-3">PM / Owner</th><th className="p-3">Calculated delivery</th><th className="p-3">Est. cost</th><th className="p-3">Progress</th><th className="p-3">Risk</th><th className="p-3 whitespace-nowrap">vs last week</th><th className="p-3">State</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-800/60">
          {projects.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400" data-testid="exec-empty">No projects match the current view.</td></tr>}
          {open.map((p) => <Row key={p.id} p={p} />)}
          {closed.length > 0 && (
            <tr data-testid="exec-completed-divider"><td colSpan={8} className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-900/40">Completed ({closed.length})</td></tr>
          )}
          {closed.map((p) => <Row key={p.id} p={p} />)}
        </tbody>
      </table>
    </div>
  );
}
